import { FC, useState } from 'react';
import { Box, VStack, HStack, Text, Badge, Button, Icon } from '@chakra-ui/react';
import {
  DialogRoot,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogActionTrigger,
} from '@/components/ui/dialog';
import { AIPrompt } from '@/types/aiManagement';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { LuStar, LuCopy, LuPencil, LuTrash2 } from 'react-icons/lu';

interface PromptCardProps {
  prompt: AIPrompt;
  onEdit?: (promptId: string) => void;
  onDuplicate?: (promptId: string) => void;
  onDelete?: (promptId: string) => void;
  onSetDefault?: (promptId: string) => void;
}

/**
 * プロンプト一覧カードコンポーネント
 */
export const PromptCard: FC<PromptCardProps> = ({
  prompt,
  onEdit,
  onDuplicate,
  onDelete,
  onSetDefault,
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const weights = prompt.weights;

  return (
    <Box
      p={5}
      borderRadius="lg"
      borderWidth={1}
      bg="white"
      shadow="sm"
      transition="all 0.2s"
      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
      opacity={prompt.isActive ? 1 : 0.6}
    >
      <VStack align="stretch" gap={3}>
        {/* ヘッダー */}
        <HStack justify="space-between">
          <HStack gap={2}>
            {prompt.isDefault && (
              <Icon as={LuStar} color="yellow.500" />
            )}
            <Text fontWeight="bold" fontSize="lg">
              {prompt.name}
            </Text>
          </HStack>
          <HStack gap={2}>
            <Badge colorPalette="blue" fontSize="xs">
              v{prompt.version}
            </Badge>
            {!prompt.isActive && (
              <Badge colorPalette="gray" fontSize="xs">
                無効
              </Badge>
            )}
          </HStack>
        </HStack>

        {/* 説明 */}
        {prompt.description && (
          <Text fontSize="sm" color="gray.600" lineClamp={2}>
            {prompt.description}
          </Text>
        )}

        {/* 重み付け表示 */}
        <HStack gap={3} flexWrap="wrap">
          <Badge colorPalette="green" variant="subtle" fontSize="xs">
            遺伝:{weights.geneticDiversity}%
          </Badge>
          <Badge colorPalette="purple" variant="subtle" fontSize="xs">
            実績:{weights.breedingHistory}%
          </Badge>
          <Badge colorPalette="orange" variant="subtle" fontSize="xs">
            特徴:{weights.traitMatch}%
          </Badge>
        </HStack>

        {/* 更新日時 */}
        <Text fontSize="xs" color="gray.500">
          更新: {format(prompt.updatedAt.toDate(), 'yyyy/MM/dd HH:mm', { locale: ja })}
        </Text>

        {/* アクションボタン */}
        <HStack gap={2} pt={2} borderTopWidth={1}>
          {onEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(prompt.id)}
              flex={1}
            >
              <Icon as={LuPencil} mr={1} />
              編集
            </Button>
          )}
          {onDuplicate && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDuplicate(prompt.id)}
              flex={1}
            >
              <Icon as={LuCopy} mr={1} />
              複製
            </Button>
          )}
          {onSetDefault && !prompt.isDefault && prompt.isActive && (
            <Button
              size="sm"
              variant="outline"
              colorPalette="yellow"
              onClick={() => onSetDefault(prompt.id)}
              flex={1}
            >
              <Icon as={LuStar} mr={1} />
              デフォルト
            </Button>
          )}
          {onDelete && !prompt.isDefault && (
            <DialogRoot
              open={isDeleteDialogOpen}
              onOpenChange={(e) => setIsDeleteDialogOpen(e.open)}
            >
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  colorPalette="red"
                  variant="outline"
                >
                  <Icon as={LuTrash2} />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>プロンプトの削除</DialogTitle>
                </DialogHeader>
                <DialogBody>
                  <Text>
                    「{prompt.name}」を削除してもよろしいですか？
                  </Text>
                  <Text mt={2} fontSize="sm" color="gray.600">
                    削除したプロンプトは一覧に表示されなくなりますが、
                    過去の分析履歴には影響しません。
                  </Text>
                </DialogBody>
                <DialogFooter>
                  <DialogActionTrigger asChild>
                    <Button variant="outline">キャンセル</Button>
                  </DialogActionTrigger>
                  <Button
                    colorPalette="red"
                    onClick={() => {
                      onDelete(prompt.id);
                      setIsDeleteDialogOpen(false);
                    }}
                  >
                    削除
                  </Button>
                </DialogFooter>
              </DialogContent>
            </DialogRoot>
          )}
        </HStack>
      </VStack>
    </Box>
  );
};
